import { actions, containers } from '@ecosystem/newron-design-system'
import { useDispatch, useSelector } from 'react-redux'
import { Answer, Category, CategoryGroup, Filter, Schema, Variable } from 'typings/filters'
import { isNil } from 'lodash'
import {
  selectSanitizedAppliedFiltersArray,
  selectSelectedFilters,
  selectFiltersChanged,
  selectVariableSelectedLabels,
  selectFiltersForTracking,
  getSavedFilters,
  Variables,
} from 'reducers/filtering/selectors'
import { useFilterCriteriaCheck } from 'hooks/useFilterCriteriaCheck'
import { resetDisabledFilters } from 'reducers/filtering/disabled'
import { getSectionInfo } from 'reducers/tracking/selectors'
import { RootState } from 'reducers/store'
import { clearAllFilters } from 'reducers/filtering/selected'
import { resetAllPageNumbers } from 'reducers/pagination/selectedPage'
import { applyFilters } from 'reducers/filtering/applied'
import { resetSubscriptionError } from 'reducers/error/subscription'
import { selectCurrentSubscriptionError } from 'reducers/error/selectors'
import { AnalyticsEvent, trackEvent, TRACKING } from 'util/analytics'
import { useFilterSchema } from 'hooks/useFilterSchema'
import { ErrorMessage } from './ErrorMessage'
import * as styles from './index.module.scss'
import { FilterErrorGuard } from './FilterErrorGuard'
import { useValidateContext } from 'hooks/useValidateContext'

export type FilterOptionType = { value: Filter; label: string }
type Props = {
  filters: CategoryGroup[]
}

export const COUNTRY_ID = 35
export const US_ANSWER_ID = 107468

const enum REPORTING_CHANNEL_VARIBALES {
  REPORTING_CHANNEL_LEVEL3 = 827,
  REPORTING_CHANNEL_LEVEL4 = 828,
  REPORTING_CHANNEL_LEVEL5 = 829,
}
export const reportingChannelVariables = [
  REPORTING_CHANNEL_VARIBALES.REPORTING_CHANNEL_LEVEL3,
  REPORTING_CHANNEL_VARIBALES.REPORTING_CHANNEL_LEVEL4,
  REPORTING_CHANNEL_VARIBALES.REPORTING_CHANNEL_LEVEL5,
]

// Variables that require exactly one Product Group and one Brand Purchased
const SINGLE_PRODUCT_AND_BRAND_REQUIRED_VARIABLES = [Variables.BRAND_ECOSYSTEM, Variables.HOME_BAND]

const calculateIsDisabled = (props: {
  isSelectedEmpty: boolean
  isAppliedEmpty: boolean
  selectedFiltersChanged: boolean
  hasAllSelected: boolean
  isUSSelectedCriteria: boolean
  productCountrySelection: boolean
  isEcosystemFilterValid: boolean
  validContext: boolean
  isProductFeatureImportanceFilterValid: boolean
  isReportingChannelFiltersApplied: boolean
}): boolean => {
  return (
    (props.isSelectedEmpty && props.isAppliedEmpty) ||
    !props.selectedFiltersChanged ||
    props.hasAllSelected ||
    props.isUSSelectedCriteria ||
    props.productCountrySelection ||
    props.isEcosystemFilterValid ||
    !props.validContext ||
    props.isProductFeatureImportanceFilterValid ||
    props.isReportingChannelFiltersApplied
  )
}

export const ProductGroupMarketFilterGuardMsg = {
  errorMsg: 'Please select a Country and Product Group',
  errorDetail:
    'At least one option in each of the Country and Product Group filters must be selected',
}

export const exceedFiltersLimitMessage = {
  errorMsg: 'Your selection has exceeded filter limits. Please update it.',
  errorDetail: 'Maximum number of filters is 60',
}

export const unitedStatesFilterGuardMsg = {
  errorMsg: 'Please only select United States as a filter',
  errorDetail: 'You can’t filter data for the United States with other countries',
}

export const brandEcosystemFilterGuardMsg = {
  errorMsg: 'Please select one Product Group and one Brand Purchased',
  errorDetail:
    'To use this filter select one option in the Product Group filter and one option in the Brand Purchased filter.',
}

export const validContextGuardMsg = {
  errorMsg: 'Please update the selected filters',
  errorDetail:
    'You must have a subscription to all country and product group combinations to apply as filters',
}

export const productGroupFilterGuardMsg = {
  errorMsg: 'Please select one Product Group',
  errorDetail: 'To use this filter select one option in the Product Group filter .',
}

export const productAndCountryFilterGuardMsg = {
  errorMsg: 'Please select one Country and Product Group',
  errorDetail:
    'To use this filter select one option in the Country filter and Product Group filter ',
}

export const FilterSummary = ({ filters }: Props) => {
  const { FilterTray } = containers
  const { filterSchema } = useFilterSchema()
  const dispatch = useDispatch()
  const subscriptionError = useSelector((state: RootState) => selectCurrentSubscriptionError(state))
  const selectedFilters = useSelector((state: RootState) =>
    selectSelectedFilters(state, { schema: filterSchema })
  )
  const isAppliedCheck =
    useSelector((state: RootState) => getSavedFilters(state)).filter((filter) => filter.isApplied)
      .length > 0
  const values: Record<number, Answer[]> = useSelector((state: RootState) => state.filters.values)
  const checkValuesIsEmpty = Object.keys(values).length === 0
  const countrySelectedFilters = selectedFilters.filter(
    (filter) => filter.variable_id === COUNTRY_ID
  )
  const productSelectedFilters = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.PRODUCT_GROUP
  )

  // Remove per-variable checks; use generic list instead
  const productFeatureImportanceFilter = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.FEATURE_IMPORTANCE
  )
  const brandPurchasedFilter = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.BRAND_PURCHASED
  )

  const reportingChannelFilters = selectedFilters.filter((filter) =>
    reportingChannelVariables?.includes(filter.variable_id)
  )

  const isUSSelectedCriteria =
    countrySelectedFilters.filter((filter) => filter.answer_id === US_ANSWER_ID).length === 1 &&
    countrySelectedFilters.length > 1

  // Generic validation for variables in the list
  const hasSingleRuleVariable = selectedFilters.some((f) =>
    SINGLE_PRODUCT_AND_BRAND_REQUIRED_VARIABLES.includes(f.variable_id)
  )
  const isEcosystemFilterValid =
    hasSingleRuleVariable &&
    !(brandPurchasedFilter.length === 1 && productSelectedFilters.length === 1)

  const isProductFeatureImportanceFilterValid =
    productFeatureImportanceFilter.length > 0 && productSelectedFilters.length !== 1

  const isReportingChannelFiltersApplied =
    reportingChannelFilters.length > 0 &&
    (productSelectedFilters.length !== 1 || countrySelectedFilters.length !== 1)

  const appliedFilters = useSelector((state: RootState) =>
    selectSanitizedAppliedFiltersArray(state, { schema: filterSchema })
  )
  const variablesWithSelections = new Set(
    useSelector((state: RootState) => selectSelectedFilters(state, { schema: filterSchema })).map(
      (item) => item.variable_id
    )
  )
  const { validContext } = useValidateContext(productSelectedFilters, countrySelectedFilters)
  const categories = filters.map((categoryGroup: CategoryGroup) => {
    return categoryGroup.categories.map((category: Category) => {
      return category
    })
  })

  const filtersubsection: string[] = []
  categories.forEach((category: Category[]) => {
    category.forEach((item: Category) => {
      item.variables.forEach((variables: Variable) => {
        if (Array.from(variablesWithSelections).includes(variables.variable_id)) {
          filtersubsection.push(item.label)
        }
      })
    })
  })
  const selectedFiltersChanged = useSelector((state: RootState) =>
    selectFiltersChanged(state, { schema: filterSchema })
  )
  const filtersApplied = useSelector((state: RootState) =>
    selectFiltersForTracking(state, { schema: filterSchema })
  )
  const { section, subSection, pageName } = useSelector((state: RootState) => getSectionInfo(state))
  const selectedFilterLabels = useSelector((state: RootState) => {
    return selectVariableSelectedLabels(state, { schema: filterSchema })
  })
  const { allSelectedCriteria } = useFilterCriteriaCheck(selectedFilters, values)
  const isSelectedEmpty = !selectedFilters.length
  const isAppliedEmpty = !appliedFilters.length
  const selectedFilterTitles = selectedFilters.map((filter) => filter.categoryGroup.split('_')[1])
  const filterSection = selectedFilterTitles
    .filter((c, index) => selectedFilterTitles.indexOf(c) === index)
    .sort()
    .join(', ')

  const filtersAppliedMessage = {
    access: TRACKING.ACCESS,
    categoryCombination: Array.from(selectedFilterLabels).sort().join(', '),
    filterName: TRACKING.FILTER_NAME,
    filterSection,
    filterSubsection: filtersubsection
      .filter((item: string, index: number) => filtersubsection.indexOf(item) === index)
      .sort()
      .join(', '),
    optionChosen: filtersApplied.flatMap((filter) => filter.optionChosen),
    pageName,
    section,
    subsection: subSection,
  }

  const handleClick = () => {
    selectedFilterLabels.forEach((label) => {
      const filtersAppliedDetailsMessage = {
        access: TRACKING.ACCESS,
        categoryChosen: label,
        categoryCombination: Array.from(selectedFilterLabels).sort().join(', '),
        filterName: TRACKING.FILTER_NAME,
        filterSection,
        filterSubsection: filtersubsection
          .filter((item: string, index: number) => filtersubsection.indexOf(item) === index)
          .sort()
          .join(', '),
        optionChosen:
          filtersApplied.find((filter) => filter.categoryName === label)?.optionChosen || [],
        pageName,
        subsection: subSection,
        section,
      }
      trackEvent({
        id: AnalyticsEvent.FiltersAppliedDetails,
        message: filtersAppliedDetailsMessage,
      })
    })
    trackEvent({
      id: AnalyticsEvent.FiltersApplied,
      message: filtersAppliedMessage,
    })

    const baseFilters =
      filterSchema === Schema.CIE
        ? selectedFilters.filter((filter) => filter.categoryGroup === 'General_Base')
        : selectedFilters.filter((filter) => filter.categoryGroup === 'ProductReview_Base')
    const syncBaseFilters =
      filterSchema === Schema.CIE
        ? (baseFilters.map((filter) => {
            return {
              ...filter,
              categoryGroup: 'ProductReview_Base',
            }
          }) as Filter[])
        : (baseFilters.map((filter) => {
            return {
              ...filter,
              categoryGroup: 'General_Base',
            }
          }) as Filter[])
    dispatch(resetSubscriptionError())
    dispatch(applyFilters([...selectedFilters, ...syncBaseFilters]))
    dispatch(resetAllPageNumbers())
  }

  const handleClearAllClick = () => {
    dispatch(resetDisabledFilters())
    dispatch(clearAllFilters())
  }

  const errorMessage =
    'The filter options you’ve chosen are not available with your subscription. Try a different combination.'

  let filterCriteriaMsg
  const productCountrySelection = !countrySelectedFilters.length || !productSelectedFilters.length

  const hasAllSelected =
    isAppliedCheck && checkValuesIsEmpty
      ? false
      : allSelectedCriteria.filterCriteria || allSelectedCriteria.exceedFiltersLimit

  if (hasAllSelected && allSelectedCriteria.exceedFiltersLimit) {
    filterCriteriaMsg = exceedFiltersLimitMessage
  }

  const hasError = !isNil(subscriptionError?.error)
  const isDisabled = calculateIsDisabled({
    isSelectedEmpty,
    isAppliedEmpty,
    selectedFiltersChanged,
    hasAllSelected,
    isUSSelectedCriteria,
    productCountrySelection,
    isEcosystemFilterValid,
    validContext,
    isProductFeatureImportanceFilterValid,
    isReportingChannelFiltersApplied,
  })
  const errorScenarios = [
    {
      id: 1,
      hasError: productCountrySelection,
      message: ProductGroupMarketFilterGuardMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 2,
      hasError: hasAllSelected,
      message: filterCriteriaMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 3,
      hasError: isUSSelectedCriteria,
      message: unitedStatesFilterGuardMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 4,
      hasError: isEcosystemFilterValid,
      message: brandEcosystemFilterGuardMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 5,
      hasError: !validContext,
      message: validContextGuardMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 6,
      hasError: isProductFeatureImportanceFilterValid,
      message: productGroupFilterGuardMsg,
      className: styles.errorMessageStyle,
    },
    {
      id: 7,
      hasError: isReportingChannelFiltersApplied,
      message: productAndCountryFilterGuardMsg,
      className: styles.errorMessageStyle,
    },
  ]
  return (
    <div className={styles.footerContainer}>
      <FilterTray.Footer>
        <section className={styles.filterSummary}>
          <div className={styles.summaryContainer}>
            <ErrorMessage
              hasError={hasError}
              message={errorMessage}
              className={styles.errorMessageStyle}
            />
            {errorScenarios.map((scenario) => (
              <FilterErrorGuard
                key={scenario.id}
                hasError={scenario.hasError}
                message={scenario.message}
                className={scenario.className}
              />
            ))}

            <div className={styles.buttonContainer}>
              <actions.Button
                onClick={handleClearAllClick}
                disabled={isSelectedEmpty}
                mode="secondary"
                stretch={true}
              >
                Clear
              </actions.Button>
              <actions.Button
                onClick={handleClick}
                disabled={isDisabled}
                mode={isDisabled ? 'secondary' : 'primary'}
                stretch={true}
              >
                Apply
              </actions.Button>
            </div>
          </div>
        </section>
      </FilterTray.Footer>
    </div>
  )
}
